import React from "react"
import {useTranslation} from "react-i18next"
import {CheckIcon} from "@heroicons/react/20/solid"
import {BoltIcon} from "@heroicons/react/24/outline"

import {GenericErrorAlert} from "../../components/Alert.tsx"
import Button from "../../components/Button.tsx"
import {LayoutBlock} from "../../components/Layout/LayoutBlock.tsx"
import {Link} from "../../components/Link.tsx"
import {Loading} from "../../components/Loading.tsx"
import {useSaaSCompanyOnboardingStatusQuery} from "../../queries/saasCompany.ts"
import {ASaasCompanyUserOnboardingStatus} from "../../services/types.generated.ts"
import {useDocumentTitle} from "../../utils/hooks.tsx"

type TOnboardingCard = {
  title: string
  description: string
  buttonText: string
  buttonLink: string
  isCompleted: boolean
}

export const OnboardingSaaSCompany: React.FC = () => {
  const {t} = useTranslation()

  useDocumentTitle(t("OnboardingSaaSCompany_DocumentTitle"))

  const {data, error, refetch} = useSaaSCompanyOnboardingStatusQuery()

  if (error) {
    return (
      <LayoutBlock outerClassName={"py-16 bg-cr-blue-super-light grow"}>
        <GenericErrorAlert retry={refetch} />
      </LayoutBlock>
    )
  }

  if (!data) {
    return <Loading size={"xl"} />
  }

  return <OnboardingDashboard onboardingData={data} />
}

export const OnboardingDashboard: React.FC<{onboardingData: ASaasCompanyUserOnboardingStatus}> = ({
  onboardingData: onboardingData,
}) => {
  const {t} = useTranslation()

  const firstSalesCycleId = onboardingData.first_ongoing_sales_cycle_id

  const cards: TOnboardingCard[] = React.useMemo(() => {
    const prospectsLink = firstSalesCycleId ? `/sellings/${firstSalesCycleId}/prospects` : "/sales-assistant"

    return [
      {
        title: t("OnboardingSaaSCompany_Card1_Title"),
        description: t("OnboardingSaaSCompany_Card1_Description"),
        buttonText: t("OnboardingSaaSCompany_Card1_Button"),
        buttonLink: prospectsLink ?? "#",
        isCompleted: onboardingData?.managed_prospects ?? false,
      },
      {
        title: t("OnboardingSaaSCompany_Card2_Title"),
        description: t("OnboardingSaaSCompany_Card2_Description"),
        buttonText: t("OnboardingSaaSCompany_Card2_Button"),
        buttonLink: "/sales-assistant",
        isCompleted: onboardingData?.used_klara_ai ?? false,
      },
      {
        title: t("OnboardingSaaSCompany_Card3_Title"),
        description: t("OnboardingSaaSCompany_Card3_Description"),
        buttonText: t("OnboardingSaaSCompany_Card3_Button"),
        buttonLink: "/dashboard",
        isCompleted: onboardingData?.visited_sales_intelligence ?? false,
      },
    ]
  }, [firstSalesCycleId, t, onboardingData])

  const completedSteps = React.useMemo(() => {
    if (!onboardingData) {
      return 0
    }
    return [
      onboardingData.managed_prospects,
      onboardingData.used_klara_ai,
      onboardingData.visited_sales_intelligence,
    ].filter(Boolean).length
  }, [onboardingData])

  const totalSteps = 3

  return (
    <LayoutBlock outerClassName={"py-16 bg-cr-blue-super-light grow"}>
      <div className={"card-shadow rounded-2xl bg-white px-6 py-8"}>
        <div className={"mb-4 flex flex-wrap items-center justify-between gap-4"}>
          <div className={"flex items-center gap-3"}>
            <BoltIcon className={"h-8 w-8 text-cr-blue"} />
            <h1 className={"text-2xl font-semibold"}>{t("OnboardingSaaSCompany_Title")}</h1>
          </div>
          <div className={"flex items-center gap-3"}>
            <div className={"h-1.5 w-28 overflow-hidden rounded-2xl bg-indigo-200"}>
              <div
                className={"h-full rounded-2xl bg-blue-700 transition-all duration-300"}
                style={{width: `${(completedSteps / totalSteps) * 100}%`}}
              />
            </div>
            <div className={"text-sm text-cr-grey-50"}>
              {t("OnboardingSaaSCompany_ProgressText", {current: completedSteps, total: totalSteps})}
            </div>
          </div>
        </div>

        <p className={"mb-8 font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
          {t("OnboardingSaaSCompany_Subtitle")}
        </p>

        <div className={"grid grid-cols-1 gap-6 md:grid-cols-3"}>
          {cards.map((card, index) => (
            <div
              key={index}
              className={
                "flex flex-col gap-4 rounded-lg bg-white p-6 outline-1 outline-offset-[-1px] outline-indigo-200"
              }
            >
              <div className={"flex items-center gap-3"}>
                <div
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-1 border-cr-blue-mid-light ${
                    card.isCompleted ? "bg-blue-700" : "bg-white"
                  }`}
                >
                  {card.isCompleted && <CheckIcon className={"h-4 w-4 text-white"} />}
                </div>
                <h2 className={"text-lg font-semibold text-cr-grey-90"}>{card.title}</h2>
              </div>
              <p className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>{card.description}</p>
              <Link to={card.buttonLink} className={"mt-auto"}>
                <Button size={"sm"} variant={"contained"} color={"blue"}>
                  {card.buttonText}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </LayoutBlock>
  )
}
